/* eslint class-methods-use-this: 0 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { uniqBy, isEqual } from 'lodash';
import classnames from 'classnames';
import DatePicker from 'components/v2/DatePicker';
import moment from 'moment-timezone';
import { titleize } from 'underscore.string';
import { withTranslation } from 'react-i18next';

import { getTimezones } from 'utils/Timezones';
import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';
import Alert from 'components/ui/Alert';
import LoadingCircle from 'components/ui/LoadingCircle';
import { showToast } from 'stores/ToastStore';

import EquipmentActionCreators from 'actions/EquipmentActionCreators';
import EquipmentStore from 'stores/EquipmentStore';
import {
  isDrayageShipment,
  isIntermodalShipmentType,
  isExportShipment,
  isDrayageOrRailShipment,
} from 'helpers/shipment-types';
import { requiresReasonCapture } from 'helpers/shipments';
import SelectReasonCodes from 'components/modules/marketplace-config/SelectReasonCodes';
import { validateChassis } from 'utils/ChassisAPIUtils';
import {
  getShipment,
  updateShipmentStopEquipment,
  updateShipment,
  updateSealNumberOnStopReferenceNumbers,
} from 'utils/ShipmentWebAPIUtils';
import { processDateTimeChange } from 'utils/formatters/Date';
import RequiredStopDocumentsList from 'components/modules/shipments/stops/RequiredStopDocumentsList';

import {
  getActionDate,
  getIntentRequirements,
  isChassisMissing,
  stopRequiresChassis,
} from 'helpers/stops';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { isFutureDate } from 'common/helpers/dateUtils';
import SoftAlertHoldNotification from 'components/modules/shipments/stops/SoftAlertHoldNotification';
import TimePicker from 'components/v2/TimePicker';
import MissingReferenceNumbers from 'components/v2/MissingReferenceNumbers';
import EquipmentTable from '../EquipmentTable';
import CargomaticModal from '../../../v2/modal';
import { PodUploader } from '../../documents/PodUploader';
import { sluggify } from '../../../../utils/localization/utils';

const SIGNATORY_MIN_LENGTH = 3;
const CHASSIS_ID_VALID_LENGTH = 10;

const SEAL_NUMBER_MIN_LENGTH = 3;
const SEAL_NUMBER_MAX_LENGTH = 10;

const getDefaultMetaState = () => ({
  signatory: '',
  date: moment(),
  time: null,
  timezone: null,
  reason: null,
  reasonCode: '',
  reasonDescription: '',
});

function validateReasonCapture({ shipper, stop, meta, t }) {
  const error = {};

  if (!requiresReasonCapture(shipper, stop, meta.date)) {
    return error;
  }

  const { reasonCode, date } = meta;

  if (!reasonCode || reasonCode === '') {
    error.value = t('SELECT_REASON_CODE');
  }

  if (!date) {
    error.value = t('ENTER_COMPLETION_DATE');
  }

  return error;
}

function validate(props, state) {
  const {
    files,
    meta: { date, signatory, reason, reasonCode },
    validDateEntered,
    validTimeEntered,
    commentRequired,
    comment,
    chassis,
    stopRequiresChassis,
    missingChassis,
    invalidChassis,
    requireCodes,
    shipment,
    shipment: { shipper },
    stop,
    stop: { action, empty, missing_docs },
    intentRequirements: { pocOptional, dateRequired },
    dateChanged,
    timeChanged,
    wasPreviouslyCompleted,
    sealNumber,
  } = state;
  const { isCarrier, t } = props;

  const error = {
    ...validateReasonCapture({ shipper, stop, meta: { reasonCode, date }, t }),
  };
  const isDrayage = isIntermodalShipmentType(shipment);

  if (stopRequiresChassis && missingChassis) {
    error.chassis = t('USE_BUTTON_FOR_ADD_CHASSIS_ERROR');
  }

  if (stopRequiresChassis && invalidChassis) {
    error.chassis = t('USE_VALID_CHASSIS_NUMBER');
  }

  if (
    stopRequiresChassis &&
    missingChassis &&
    action === 'pickup' &&
    chassis === ''
  ) {
    error.chassis = t('ADD_CHASSIS_NBR_TO_COMPLETE_STOP');
  }

  if (
    stopRequiresChassis &&
    missingChassis &&
    action === 'pickup' &&
    chassis.length &&
    chassis.length !== 10
  ) {
    error.chassis = t('CHASSIS_NBR_10_DIGIT_ERROR');
  }

  const fileMissing = files.length === 0;
  if (!pocOptional && fileMissing) {
    // SSR - Remove drayage case from conditional when SSR is live
    if (
      !isDrayage ||
      (!featureEnabled(CONFIGURED_FEATURES.SSR) &&
        isDrayage &&
        action === 'deliver' &&
        !empty)
    ) {
      error.file = t('UPLOAD_COMPLETION_PROOF_ERROR');
    }
  }

  if (isCarrier && !isDrayage && !files.length) {
    if (action === 'deliver') {
      error.file = t('UPLOAD_COMPLETION_PROOF_ERROR');
    }
  }

  // SSR - Remove case when SSR is live
  if (
    !featureEnabled(CONFIGURED_FEATURES.SSR) &&
    !files.length &&
    missing_docs
  ) {
    error.file = t('UPLOAD_COMPLETION_PROOF_ERROR');
  }

  // date is required if a POC is provided
  if (
    (files.length ||
      dateRequired ||
      (!dateChanged && !wasPreviouslyCompleted)) &&
    !validDateEntered
  ) {
    error.date = t('ENTER_COMPLETION_DATE');
  }

  if (
    (validDateEntered && !validTimeEntered) ||
    (!timeChanged && !wasPreviouslyCompleted)
  ) {
    error.time = t('ENTER_COMPLETION_DATE');
  }

  if (isCarrier && signatory === '' && !files.length) {
    error.signatory = t('ENTER_SIGNATORY');
  }
  if (!pocOptional && !signatory) {
    if (!isDrayage || (isDrayage && action === 'deliver' && !empty)) {
      error.signatory = t('ENTER_SIGNATORY');
    }
  } else if (signatory && signatory.length < SIGNATORY_MIN_LENGTH) {
    error.signatory = t('MIN_LENGTH_CHARS_SIGNATORY', {
      signatoryMinimumLength: SIGNATORY_MIN_LENGTH,
    });
  }

  if (requireCodes) {
    if (!reason) {
      error.custom_reason_code = t('REASON_CODES_REQUIRED_TO_COMPLETE_STOP');
    }

    if (commentRequired && !comment.length) {
      error.custom_reason_code = t('COMMENT_REQUIRED_FOR_REASON_CODE');
    }
  }

  if (
    isExportShipment(shipment) &&
    !wasPreviouslyCompleted &&
    (action === 'pickup' || action === 'deliver') &&
    !empty &&
    (!sealNumber || sealNumber === '')
  ) {
    error.sealNumber = t('SEAL_NUMBER_MISSING');
  }

  if (
    sealNumber &&
    (sealNumber.length < SEAL_NUMBER_MIN_LENGTH ||
      sealNumber.length > SEAL_NUMBER_MAX_LENGTH)
  ) {
    error.sealNumber = t('SEAL_NUMBER_CHARACTER_COUNT');
  }

  return error;
}

function getInitialState() {
  return {
    files: [],
    meta: getDefaultMetaState(),
    error: {},
    completeDisabled: false,
    uploading: false,
    validDateEntered: true,
    validTimeEntered: false,
    isDateInvalid: false,
    uploadedPods: 0,
    uploadPodFailures: 0,
    validPods: 0,
    comment: '',
    commentRequired: false,
    requireCodes: false,
    missingChassis: false,
    equipments: [],
    chassis: '',
    invalidChassis: false,
    stopRequiresChassis: false,
    selectedChassis: null,
    invalidPdfs: [],
    reasonCodeStarted: false, // temporary reason code fix, remove later
    addedMissingReferenceNumbers: {},
    currentStopDocumentState: [],
  };
}

class StopCompleteModal extends Component {
  static propTypes = {
    stopId: PropTypes.string.isRequired,
    shipmentId: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      ...getInitialState(),
      loaded: false,
      shipperStopDocumentRequirements: props.shipperStopDocumentRequirements,
      dateChanged: false,
      timeChanged: false,
    };
  }

  isPODRequired() {
    if (featureEnabled(CONFIGURED_FEATURES.SSR)) {
      // When SSR goes live, we can deprecate this isPODRequired function because stops should be able to enter provide documentation state
      return false;
    }
    const { stop, shipment } = this.state;
    if (stop && shipment) {
      return (
        stop.missing_docs &&
        stop.action === 'deliver' &&
        (this.state.meta.signatory === '' || !this.state.meta.signatory) &&
        (this.state.files.length === 0 || !this.state.files)
      );
    }
    return false;
  }

  async componentDidMount() {
    await this.fetchShipment();
    EquipmentStore.addChangeListener(this.getCreatedEquipment);
    EquipmentStore.addChangeListener(this.getCreateEquipmentError);
  }

  componentWillUnmount() {
    EquipmentStore.removeChangeListener(this.getCreatedEquipment);
    EquipmentStore.removeChangeListener(this.getCreateEquipmentError);
  }

  UNSAFE_componentWillReceiveProps(props) {
    if (
      !this.props.show &&
      props.show &&
      props.shipmentId &&
      props.stopId &&
      props.shipmentId !== this.props.shipmentId &&
      props.stopId !== this.props.stopId
    ) {
      this.fetchShipment();
    }
    if (
      featureEnabled(CONFIGURED_FEATURES.SSR) &&
      props?.shipperStopDocumentRequirements &&
      !isEqual(
        props.shipperStopDocumentRequirements,
        this.props.shipperStopDocumentRequirements,
      )
    ) {
      this.setState({
        shipperStopDocumentRequirements: props.shipperStopDocumentRequirements,
      });
    }
  }

  validate() {
    const {
      meta: { date, timezone },
    } = this.state;
    const usingFutureDate = isFutureDate(date, timezone);
    this.setState((state) => {
      const error = validate(this.props, state);
      let completeDisabled = Object.keys(error).length > 0;
      if (this.isPODRequired() || usingFutureDate) {
        completeDisabled = true;
      }
      return { error, completeDisabled, usingFutureDate };
    });
  }

  fetchShipment = async () => {
    const { shipmentId, stopId, isCarrier } = this.props;

    try {
      const {
        entity: { item: shipment },
      } = await getShipment(shipmentId);

      const stop = shipment.stops.find((s) => s._id === stopId);
      const completeDisabled = stop?.missing_docs || false;
      const equipments = uniqBy(
        (stop.equipments || []).map((eq) => ({
          ...eq,
          canRemove: false,
        })),
        (eq) => eq._id,
      );

      const intentRequirements = getIntentRequirements(
        shipment,
        stop,
        !isCarrier,
      );
      const missingChassis = isChassisMissing({
        shipment,
        equipments,
        isCarrier,
      });
      let availableEquipment;
      if (missingChassis) {
        availableEquipment = uniqBy(
          (shipment?.order?.equipments || [])
            .filter((eq) => eq._equipmentType === 'chassis')
            .map((eq) => ({ ...eq, canAdd: true })),
          (eq) => eq._id,
        );
      } else {
        availableEquipment = [];
      }

      const { date, timezone } = getActionDate(stop);
      const wasPreviouslyCompleted = !!stop?.timeline?.complete?.meta?.date;
      const reasonCode = stop?.timeline?.complete?.meta?.reasonCode || '';
      const time = date ? date.format('hh:mm A') : null;

      const currentSealNumber = stop.reference_numbers?.find(
        (x) => x.name === 'seal_number',
      )?.value;

      if (currentSealNumber) {
        this.setState({
          initialSealNumber: currentSealNumber,
          sealNumber: currentSealNumber,
        });
      }

      this.setState(
        (state) => {
          return {
            completeDisabled,
            missingChassis: isChassisMissing({
              shipment,
              equipments,
              isCarrier,
            }),
            stopRequiresChassis: stopRequiresChassis({ shipment, isCarrier }),
            shipment,
            availableEquipment,
            stop,
            wasPreviouslyCompleted,
            equipments,
            validDateEntered: true,
            validTimeEntered: true,
            meta: {
              ...state.meta,
              reasonCode,
              date: date,
              time,
              timezone: timezone,
            },
            loaded: true,
            intentRequirements,
          };
        },
        () => {
          this.validate();
        },
      );
    } catch (e) {
      console.error(e);
    }
  };

  async updateStopEquipment(equipment) {
    if (equipment._equipmentType !== 'chassis') {
      return;
    }

    const { isCarrier } = this.props;
    const { shipment, stop } = this.state;
    const { receivables, payables } = shipment;
    const updates = {
      equipments: uniqBy([...this.state.equipments], (eq) => eq._id),
    };

    if (!this.props.isCarrier) {
      updates.receivables = receivables;
      updates.payables = payables;
    }

    try {
      const {
        entity: { job },
      } = await updateShipmentStopEquipment(shipment, stop, updates, isCarrier);

      const container = (stop.equipments || []).find(
        (eq) => eq._equipmentType === 'container',
      );
      if (container) {
        const stopsWithSameContainer = shipment.stops.filter((selectedStop) => {
          if (selectedStop._id === stop._id) {
            return false;
          }
          const stopEquipments = selectedStop.equipments || [];
          const hasChassis = stopEquipments.some(
            (eq) => eq._equipmentType === 'chassis',
          );

          return (
            !hasChassis &&
            stopEquipments.some(
              (eq) => eq.equipment_id === container.equipment_id,
            )
          );
        });

        const promiseMap = stopsWithSameContainer.map((selectedStop) => {
          const updates = {
            equipments: [...(selectedStop.equipments || []), equipment],
          };
          if (!isCarrier) {
            updates.receivables = receivables;
            updates.payables = payables;
          }
          return updateShipmentStopEquipment(
            shipment,
            selectedStop,
            updates,
            isCarrier,
          );
        });
        if (promiseMap.length > 0) {
          const results = await Promise.all(promiseMap);
          const job = results[results.length - 1].entity.job;
          this.props.onStopEquipmentUpdateSuccess({
            job,
          });
        } else {
          this.props.onStopEquipmentUpdateSuccess({ job });
        }
      } else {
        this.props.onStopEquipmentUpdateSuccess({ job });
      }
    } catch (e) {
      this.props.onStopEquipmentUpdateFail(e);
      console.error(e);
    }
  }

  // Apply Chassis to all stops with matching containers
  getCreatedEquipment = () => {
    // Get newly created chassis
    const equipment = EquipmentStore.getCreatedEquipment();
    if (!equipment || equipment._equipmentType !== 'chassis') {
      return;
    }

    const equipmentAdded = this.state.equipments.find(
      (eq) => eq.equipment_id === equipment.equipment_id,
    );

    if (equipmentAdded) {
      return;
    }

    this.setState(
      (prevState) => {
        const { error } = prevState;
        delete error.chassis;

        return {
          equipments: [
            ...prevState.equipments,
            { ...equipment, canRemove: false },
          ],
          missingChassis: false,
          invalidChassis: false,
          loading: false,
          error,
        };
      },
      () => {
        this.validate();
        this.updateStopEquipment(equipment);
      },
    );
  };

  getCreateEquipmentError = () => {
    const error = EquipmentStore.getCreateEquipmentError();
    if (error && sluggify(error.message) === 'EQUIPMENT_MISMATCH') {
      showToast(
        this.props.t('EQUIPMENT_MISMATCH'),
        this.props.t('EQUIPMENT_MISMATCH_ERROR_MSG', {
          equipmentId: error.existing_equipment.equipment_id,
          equipmentType: error.existing_equipment._equipmentType,
        }),
        'error',
      );
      this.setState((prevState) => {
        return {
          error: {
            ...prevState.error,
            chassis: this.props.t('CHASSIS_EQUIPMENT_MISMATCH'),
          },
        };
      });
    }
  };

  getProofOfCompletionLabel() {
    const action = this.state.stop.action;
    const { t } = this.props;
    if (action === 'pickup') return t('PROOF_OF_PICKUP');
    if (action === 'deliver') return t('PROOF_OF_DELIVERY');
    if (action === 'return') return t('PROOF_OF_RETURN');

    return '';
  }

  setCodesRequired = (requireCodes) => {
    this.setState({ requireCodes });
  };

  handleEquipmentRemove(equipment) {
    if (equipment._equipmentType !== 'chassis') {
      return;
    }

    const { shipment } = this.state;
    const { isCarrier } = this.props;

    this.setState(
      (prevState) => {
        const equipments = prevState.equipments.filter(
          (e) => e._id !== equipment._id,
        );
        return {
          missingChassis: isChassisMissing({ shipment, equipments, isCarrier }),
          selectedChassis: null,
          equipments,
          availableEquipment: [...prevState.availableEquipment, equipment].map(
            (eq) => ({ ...eq, canAdd: true }),
          ),
        };
      },
      () => this.validate(),
    );
  }

  handleEquipmentSelect(equipment) {
    if (equipment._equipmentType !== 'chassis') {
      return;
    }
    this.setState(
      (prevState) => {
        const equipments = uniqBy(
          [...prevState.equipments, { ...equipment, canRemove: true }],
          (eq) => eq._id,
        );
        const { shipment } = prevState;
        const { isCarrier } = this.props;

        return {
          selectedChassis: equipment,
          missingChassis: isChassisMissing({ shipment, equipments, isCarrier }),
          equipments,
          availableEquipment: prevState.availableEquipment
            .filter((eq) => eq._id !== equipment._id)
            .map((eq) => ({ ...eq, canAdd: false })),
        };
      },
      () => this.validate(),
    );
  }

  handleReasonSelect = (item) => {
    const code = item?.code;
    const description = item?.description;
    this.setState(
      (state) => {
        const meta = {
          ...state.meta,
          reason: item,
          reasonDescription: description,
          reasonCode: code,
        };
        return {
          meta,
          reasonCodeStarted: code && description, // temporary reason code fix, remove later
        };
      },
      () => this.validate(),
    );
  };

  handleCommentInput = (comment) => {
    this.setState({ comment }, () => this.validate());
  };

  handleCommentRequired = (value) => {
    this.setState({ commentRequired: value }, () => this.validate());
  };

  handleSignatoryChange = (signatory) => {
    this.setState(
      (state) => {
        const meta = {
          ...state.meta,
          signatory,
        };

        return { meta };
      },
      () => this.validate(),
    );
  };

  handleTimeChange = (e) => {
    const {
      target: { value: time },
    } = e;
    this.setState(
      (prevState) => {
        const result = processDateTimeChange({
          date: prevState.meta.date,
          time,
          timezone: prevState.meta.timezone,
        });
        const {
          date,
          isDateInvalid,
          validTimeEntered,
          validDateEntered,
          usingFutureDate,
        } = result;
        const meta = {
          ...prevState.meta,
          date,
          time,
        };

        return {
          meta,
          validDateEntered,
          validTimeEntered,
          isDateInvalid,
          usingFutureDate,
          timeChanged: true,
        };
      },
      () => this.validate(),
    );
  };

  handleDateChange = (incomingDate) => {
    this.setState(
      (prevState) => {
        const result = processDateTimeChange({
          date: incomingDate,
          time: prevState.meta.time,
          timezone: prevState.meta.timezone,
        });
        const {
          date,
          isDateInvalid,
          validTimeEntered,
          validDateEntered,
          usingFutureDate,
        } = result;
        const meta = {
          ...prevState.meta,
          date,
        };

        return {
          meta,
          validDateEntered,
          validTimeEntered,
          isDateInvalid,
          usingFutureDate,
          dateChanged: true,
        };
      },
      () => this.validate(),
    );
  };

  handleReferenceNumberChange = (refNumName, refNumValue) => {
    this.setState(
      (state) => {
        const incomingReferenceNumber = {
          name: refNumName,
          value: refNumValue,
          custom: false,
          editable: true,
          optimized: refNumValue?.toUpperCase()?.trim(),
        };

        const addedMissingReferenceNumbers = {
          ...state.addedMissingReferenceNumbers,
          [refNumName]: incomingReferenceNumber,
        };

        return { addedMissingReferenceNumbers };
      },
      () => this.validate(),
    );
  };

  handleUpdateShipmentReferenceNumbers = async () => {
    const { addedMissingReferenceNumbers, shipment } = this.state;
    try {
      if (Object.keys(addedMissingReferenceNumbers).length) {
        const refNumsToAdd = Object.keys(addedMissingReferenceNumbers).map(
          (key) => addedMissingReferenceNumbers[key],
        );
        await updateShipment(shipment, {
          reference_numbers: [...shipment.reference_numbers, ...refNumsToAdd],
        });
      }
    } catch (err) {
      console.error('Error while updating shipment', err);
    }
  };

  completeAfterAllChecks = async (marketRateData) => {
    const {
      commentRequired,
      comment,
      equipments,
      meta: currentMeta,
      stop,
      shipment,
    } = this.state;

    const { ...meta } = currentMeta;

    if (isDrayageShipment(shipment)) {
      const container = equipments.find(
        (eq) => eq._equipmentType === 'container',
      );
      if (container) {
        meta.containerId = container.equipment_id;
      }
      const chassis = equipments.find((eq) => eq._equipmentType === 'chassis');
      if (chassis) {
        meta.chassis = chassis.equipment_id;
      }
      const trailer = equipments.find((eq) => eq._equipmentType === 'trailer');
      if (trailer) {
        meta.trailerId = trailer.equipment_id;
      }
    }

    meta.date = moment(meta.date).toISOString();

    if (commentRequired && comment && comment.length) {
      meta.message = comment;
    }

    const pods = this.state.files;
    if (pods.length) {
      meta.files = pods.map((document) => {
        return {
          key: document.key,
          id: document.id,
          bucket: document.bucket,
          type: document.type,
        };
      });
    }

    if (this.state.selectedChassis) {
      await this.updateStopEquipment(this.state.selectedChassis);
    }
    let yard;
    if (stop.yardId && marketRateData) {
      yard = this.props.yards.find((y) => y.id === stop.yardId).nameInternal;
      // rate and freedays comes from getMarketShipperRate or response from updateMarketShipperRate
      const { rate, freeDays } = marketRateData;
      meta.metering = {
        yard,
        rate,
        freeDays,
      };
    }
    this.props.onStopComplete(this.state.shipment, stop, [], meta);
    this.onHide();
  };

  handleComplete = async () => {
    const { shipment, sealNumber, stop, initialSealNumber } = this.state;
    const { marketShipperRate } = this.props;
    const error = validate(this.props, this.state);
    if (Object.keys(error).length > 0) {
      return this.setState({ error });
    }

    // rates are coming from shipment page itself
    await this.completeAfterAllChecks(marketShipperRate);
    if (isIntermodalShipmentType(shipment)) {
      await this.handleUpdateShipmentReferenceNumbers();
    }

    if (sealNumber && sealNumber !== '' && sealNumber !== initialSealNumber) {
      try {
        await updateSealNumberOnStopReferenceNumbers(
          shipment?._id?.toString(),
          stop?._id?.toString(),
          sealNumber,
        );
      } catch (_err) {
        console.error('Failed to Update Seal Number');
      }
    }
  };

  handleChassisChange = (chassis) => {
    this.setState({ chassis }, () => this.validate());
  };

  handleSealNumberChange = (sealNumber) => {
    this.setState({ sealNumber: sealNumber }, () => this.validate());
  };

  addChassis = async () => {
    const { chassis } = this.state;

    try {
      const results = await validateChassis(chassis);
      const { validated } = results;

      if (validated) {
        const equipment = {
          equipment_id: chassis.trim(),
          _equipmentType: 'chassis',
          _equipmentSize: '',
          _type: '',
          status: 'N/A',
        };
        this.setState({ loading: false });
        return EquipmentActionCreators.addEquipment(equipment);
      }
      return this.setState({ invalidChassis: true }, this.validate);
    } catch (_e) {
      this.setState((state) => {
        const { error } = state;
        error.chassis = this.props.t('ERROR_VALIDATING_CHASSIS');
        return { error };
      });
    }
  };

  onHide = () => {
    this.setState(getInitialState());
    this.props.onHide();
  };

  renderSealNumber() {
    const { t } = this.props;

    if (this.state.wasPreviouslyCompleted) return null;
    if (!isDrayageOrRailShipment(this.state.shipment)) return null;

    return (
      <>
        <hr data-testid="hr-ec565bf6" className="my-4" />
        <div data-testid="div-9f7852b8" className="mb-4">
          <CargomaticInput
            dataTestId="CargomaticInput-87418fb7"
            id="seal-number"
            label={t('SEAL_NUMBER')}
            type="text"
            name="stop-complete-seal-number"
            value={this.state.sealNumber}
            placeholder={t('SEAL_NUMBER')}
            onChange={this.handleSealNumberChange}
            error={{ message: this.state.error?.sealNumber }}
          />
        </div>
      </>
    );
  }

  // Displays equipment currently attached to stop
  renderEquipment() {
    const { t } = this.props;
    const { equipments, missingChassis, stopRequiresChassis } = this.state;
    if (equipments.length === 0 || !stopRequiresChassis) {
      return null;
    }

    const {
      stop: { action },
      loading,
    } = this.state;

    if (action === 'deliver' && equipments.length === 0) {
      return null;
    }

    if (loading) {
      return <div data-testid="div-fea6eced">{t('UPDATING_EQUIPMENT')}</div>;
    }
    const { error, chassis } = this.state;
    const chassisPlaceHolder = t('CHASSIS_NUMBER_REQUIRED');
    return (
      <div data-testid="div-a084ac5d">
        <hr data-testid="hr-1a7d71dc" />
        <h4 data-testid="h4-e0197c84" className="my-0 mb-4">
          {t('EQUIPMENT')}
        </h4>
        {missingChassis && (
          <div data-testid="div-13a45677" className="mb-4">
            <CargomaticInput
              dataTestId="CargomaticInput-0406967e"
              error={{ message: error.chassis }}
              className="mb-2"
              label={`${t('CHASSIS_NUMBER_LENGTH', {
                validLength: CHASSIS_ID_VALID_LENGTH,
              })} :`}
              type="text"
              placeholder={chassisPlaceHolder}
              onChange={this.handleChassisChange}
              value={chassis}
            />
            <button
              data-testid="button-e200eb3c"
              className="mt-2 button button-green"
              onClick={() =>
                this.setState({ loading: true }, () => this.addChassis())
              }
              disabled={chassis.length !== CHASSIS_ID_VALID_LENGTH}
            >
              {t('ADD_CHASSIS')}
            </button>
          </div>
        )}
        <h4 data-testid="h4-b8623817" className="my-0 mb-2">
          {t('CURRENT_EQUIPMENT')}
        </h4>
        <EquipmentTable
          equipments={equipments}
          shipmentId={this.props.shipmentId}
          availableEquipment={this.state.availableEquipment}
          onAdd={(equipment) => this.handleEquipmentSelect(equipment)}
          onRemove={(equipment) => this.handleEquipmentRemove(equipment)}
        />
      </div>
    );
  }

  renderReasonCapture() {
    if (!this.state.loaded) {
      return null;
    }
    const { shipment } = this.state;

    return (
      <div data-testid="div-41e7b61b" className="mt-3 mb-4">
        <SelectReasonCodes
          shipment={shipment}
          category="stop_complete"
          updateComment={this.handleCommentInput}
          selectReasonCode={this.handleReasonSelect}
          updateCommentRequired={this.handleCommentRequired}
          setCodesRequired={this.setCodesRequired}
        />
      </div>
    );
  }

  handleTimezoneChange = (zone) => {
    const timezone = zone.value;

    this.setState(
      (prevState) => {
        const result = processDateTimeChange({
          date: prevState.meta.date,
          time: prevState.meta.time,
          timezone,
        });
        const {
          date,
          isDateInvalid,
          validTimeEntered,
          validDateEntered,
          usingFutureDate,
        } = result;
        const meta = {
          ...prevState.meta,
          date,
          timezone,
        };

        return {
          meta,
          validDateEntered,
          validTimeEntered,
          isDateInvalid,
          usingFutureDate,
        };
      },
      () => this.validate(),
    );
  };

  refreshStopDataOnFileUploaded = (uploadedFiles) => {
    try {
      this.setState((currentState) => {
        return {
          ...currentState,
          files: currentState.files.concat(uploadedFiles),
        };
      }, this.fetchShipment);
    } catch (e) {
      console.error(`Error refreshing stop data: ${e.message}`);
    }
  };

  updateCurrentStopDocumentsState = (currentStopDocumentState) => {
    this.setState({ currentStopDocumentState }, this.validate);
  };

  renderDocumentUploader = () => {
    const { t } = this.props;
    const {
      shipment,
      stop,
      meta,
      intentRequirements,
      files,
      error,
      shipperStopDocumentRequirements,
    } = this.state;

    if (
      shipment &&
      stop &&
      featureEnabled(CONFIGURED_FEATURES.SSR) &&
      isIntermodalShipmentType(shipment)
    ) {
      return (
        <RequiredStopDocumentsList
          shipment={shipment}
          stop={stop}
          shipperStopDocumentRequirements={shipperStopDocumentRequirements}
          onFileUploaded={this.refreshStopDataOnFileUploaded}
          onStopDocumentStateUpdate={this.updateCurrentStopDocumentsState}
        />
      );
    }

    return (
      <div data-testid="div-72dc30f5" className="flex flex-col">
        <p data-testid="p-37903541" className="text-sm mb-2">
          {t('POC_DOCUMENT_LOCATION')}
        </p>
        <PodUploader
          shipment={{ id: shipment._id }}
          stop={stop}
          meta={meta}
          intentRequirements={intentRequirements}
          error={error}
          onProcessing={(processing) => {
            this.setState({
              uploading: processing,
            });
          }}
          onUpload={(shipment, stop, type, uploadedFile) => {
            const prevFiles = files || [];
            this.setState(
              {
                files: [...prevFiles, uploadedFile],
              },
              this.validate,
            );
          }}
        />
      </div>
    );
  };

  renderCaptureDetails() {
    const { t } = this.props;
    const action = this.state.stop.action === 'pickup' ? 'pickup' : 'delivery';
    const temporaryReasonCodeCheck = validateReasonCapture({
      shipper: this.state.shipment.shipper,
      stop: this.state.stop,
      meta: {
        reasonCode: this.state.meta.reasonCode,
        date: this.state.meta.date,
      },
      t,
    });
    return (
      <div data-testid="div-782d9c93">
        <div data-testid="div-4ea12fb9" id="sign-section" className="mt-4">
          <CargomaticInput
            dataTestId="CargomaticInput-3f3a890e"
            id="signatory"
            label={t('SIGNATORY')}
            type="text"
            value={this.state.meta.signatory}
            placeholder={t('SIGNATORY_NAME')}
            onChange={this.handleSignatoryChange}
            error={{ message: this.state.error.signatory }}
          />
        </div>
        {(!this.state.wasPreviouslyCompleted ||
          temporaryReasonCodeCheck.value ||
          this.state.reasonCodeStarted) && (
          <div data-testid="div-ca269fe1">
            <hr data-testid="hr-874556dd" />
            <h4 data-testid="h4-9e30db41" className="my-0 mb-2">
              {titleize(t(sluggify(action)))} {t('INFO_LOCAL_TIME')}
            </h4>
            <p data-testid="p-a9f9ce60" className="mb-2 text-sm">
              {t('SPECIFY_TIME_DRIVER_COMPLETED')}
            </p>
            <div
              data-testid="div-e9dac94f"
              className="flex flex-wrap items-center -mx-2"
            >
              <div data-testid="div-ba04ed4b" className="px-2">
                <DatePicker
                  dataTestId="DatePicker-573805ca"
                  className={{
                    error: this.state.error.date,
                  }}
                  placeholderText={t('COMPLETION_DATE')}
                  selected={this.state.dateChanged ? this.state.meta.date : ''}
                  onChange={(selectedDate) => {
                    this.handleDateChange(selectedDate);
                  }}
                  onChangeRaw={(e) => {
                    const {
                      target: { value },
                    } = e;
                    this.handleDateChange(value);
                  }}
                  isClearable
                />
              </div>
              <div data-testid="div-d3bbcda8" className="px-2">
                <TimePicker
                  dataTestId="TimePicker-d936bf9e"
                  onChange={this.handleTimeChange}
                  value={this.state.timeChanged ? this.state.meta.time : ''}
                  className={classnames({ error: this.state.error.time })}
                />
              </div>
              <div data-testid="div-0a20daf6" className="px-2">
                <CargomaticSelect
                  dataTestId="CargomaticSelect-ec0baf63"
                  hideLabel
                  onSelect={this.handleTimezoneChange}
                  items={getTimezones()}
                  value={this.state.meta.timezone}
                  valueKey="value"
                  textKey="name"
                />
              </div>
            </div>
            <div data-testid="div-2b351c4b">
              {this.state.usingFutureDate && this.state.dateChanged && (
                <div
                  data-testid="div-290e999a"
                  className="text-red text-sm mt-2"
                >
                  {t('COMPLETION_DATE_FUTURE_WARNING')}
                </div>
              )}
            </div>
            <div data-testid="div-45503a00">
              {this.state.isDateInvalid && (
                <div data-testid="div-f21b3a33" className="mt-2 text-red">
                  {t('INVALID_DATE')}
                </div>
              )}
            </div>
            {this.renderReasonCapture()}
          </div>
        )}
        {isIntermodalShipmentType(this.state.shipment) && (
          <MissingReferenceNumbers
            dataTestId="MissingReferenceNumbers-3e1c533e"
            shipment={this.state.shipment}
            stop={this.state.stop}
            onInputChange={this.handleReferenceNumberChange}
          />
        )}
      </div>
    );
  }

  renderErrors() {
    const { error } = this.state;
    const { invalidPdfs } = this.state;
    const { t } = this.props;

    const errorKeys = Object.keys(error);
    if (errorKeys.length === 0 && invalidPdfs.length === 0) {
      return null;
    }
    return (
      <Alert dataTestId="Alert-e40cba14" type="error" className="mb-4">
        {invalidPdfs.length > 0 && (
          <p data-testid="p-3c876ff8">{t('PDF_NOT_UPLOADED_TRY_AGAIN')}</p>
        )}
        <ul data-testid="ul-82b008b2">
          {invalidPdfs.map((fileName, index) => (
            <li data-testid={`li-cfd4cdda-${index}`} key={fileName}>
              {fileName}
            </li>
          ))}
          {errorKeys.map((key, index) => (
            <li data-testid={`li-1a5cc607-${index}`} key={key}>
              {error[key]}
            </li>
          ))}
        </ul>
      </Alert>
    );
  }

  renderSoftAlertForHolds() {
    const { shipment } = this.state;
    if (!shipment || !shipment.holds) return null;
    return <SoftAlertHoldNotification shipment={shipment} />;
  }

  renderBody() {
    const { t } = this.props;
    if (!this.state.loaded) {
      return (
        <LoadingCircle
          dataTestId="LoadingCircle-76f8ea38"
          text={t('LOADING')}
        />
      );
    }

    return (
      <div data-testid="div-2924001d">
        {this.renderSoftAlertForHolds()}
        {this.renderErrors()}
        {this.renderDocumentUploader()}
        {this.renderCaptureDetails()}
        {this.renderSealNumber()}
        {this.renderEquipment()}
      </div>
    );
  }

  renderFooter() {
    const {
      loaded,
      stop,
      completeDisabled,
      uploadedPods,
      uploading,
      dateChanged,
      timeChanged,
      wasPreviouslyCompleted,
    } = this.state;
    const isDelivery = stop && stop.action === 'deliver' && !stop.empty;
    const { t } = this.props;

    const shouldDisableForDate =
      !wasPreviouslyCompleted && (!dateChanged || !timeChanged);

    const completeButtonClass = classnames({
      button: true,
      'button-green': true,
      'stop-stopCompleteModal-uploadPOD': isDelivery && uploadedPods > 0,
    });

    if (!loaded) {
      return null;
    }
    return (
      <div data-testid="div-08d53034" className="flex justify-end space-x-2">
        <button
          data-testid="button-b25f812e"
          type="button"
          className="button button-secondary"
          onClick={this.onHide}
        >
          {t('CANCEL')}
        </button>
        <button
          data-testid="button-e842cd3c"
          id="completeStop"
          disabled={completeDisabled || uploading || shouldDisableForDate}
          className={completeButtonClass}
          type="button"
          onClick={this.handleComplete}
        >
          {t('COMPLETE')}
        </button>
      </div>
    );
  }

  render() {
    const { loaded } = this.state;
    const { t } = this.props;
    const title = loaded
      ? this.getProofOfCompletionLabel()
      : t('COMPLETE_STOP');

    return (
      <CargomaticModal
        dataTestId="CargomaticModal-3290452e"
        show={this.props.show}
        backdrop="static"
        onHide={this.onHide}
        showCloseButton
        title={title}
        footer={this.renderFooter()}
      >
        {this.renderBody()}
      </CargomaticModal>
    );
  }
}

StopCompleteModal.propTypes = {
  show: PropTypes.bool.isRequired,
  shipmentId: PropTypes.string.isRequired,
  stopId: PropTypes.string.isRequired,
  onHide: PropTypes.func.isRequired,
  onStopComplete: PropTypes.func.isRequired,
};

export default withTranslation()(StopCompleteModal);
